import {ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {BehaviorSubject, combineLatest, merge, Observable, of, ReplaySubject, Subject} from 'rxjs';
import * as moment from 'moment-timezone';
import {Moment} from 'moment-timezone';
import {catchError, distinctUntilChanged, filter, first, map, shareReplay, switchMap, takeUntil} from 'rxjs/operators';
import {
    ECEconomicsTemplateType,
    ECMemberParticipationType,
    ECMode,
    EnergyCommunity,
    EntitySubType,
    EntityType
} from 'smag-commons/cfg/hierarchy';
import {
    ECCacvRedistribution,
    ECCacvRedistributionMode,
    ECCosts,
    ECCostType,
    ECEconomicsAggregatedCosts,
    ECEconomicsAggregatedEnergy,
    ECEconomicsAggregatedIncentivation,
    ECEconomicsAggregatedRedistribution,
    ECEconomicsAggregatedResponse,
    ECEconomicsAggregatedTimelyData,
    ECEconomicsParameters,
    ECEconomicsSurplusPremiumRate,
    EconomicsErrorType,
    ECSurplusPremiumRateScope
} from 'smag-commons/dto/eco';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from '@angular/material/core';
import {MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter} from '@angular/material-moment-adapter';
import {AuthService} from 'auth-commons';
import {Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {ValueObjectType} from 'smag-commons/cfg/value';
import {Permission, Scope} from 'smag-commons/dto/api';
import {EconomicsConfig, TimeConfig} from 'smag-commons/cfg';
import {HierarchyEntity, HierarchyService} from '../../../shared/hierarchy.service';
import {GseData} from './gse-data/ec-gse-data.component';
import {DialogService} from '../../../shared/dialog/dialog.service';
import {ChartLine} from '../../../shared/line-chart/line-chart.component';
import {NiceValuePipe} from '../../../shared/shared.pipe';
import {hasError, showError} from '../../../shared/economics-errors';
import {APP_CONFIG_TOKEN, AppConfig} from '../../../app.config';
import {SmagApiService} from '../../../api/smag-api.service';
import {MsgBoxService} from '../../../shared/msg-box.service';
import {ConfigurationService} from '../../../shared/configuration.service';
import {I18NService} from '../../../i18n/i18n.service';
import {downloadBlob, getSum, isDefined, isNumber} from '../../../commons/commons';
import {can} from '../../../shared/permissions';
import {mkMode} from '../../../shared/energy-community';
import {BillingPeriod, BillingPeriodConfig} from '../billing-filter/ec-billing-filter.component';
import {TimeFilterType} from '../../../shared/time-filter/time-filter.component';
import {YearlyValue} from 'smag-commons/cfg/value/yearly-value';
import {CACV_MODE, COSTS, MEMBERSHIP_FEE} from 'smag-commons/cfg/economics-values';
import {CONTENT_DIALOG} from './ec-select-date/ec-select-date.component';
import {
    allGSE,
    AxisChartOptions,
    CACV_STRING,
    consolidationsAvailable,
    CPR_BT_STRING,
    CPR_MT_STRING,
    DataSourceInfo,
    getDataSourceIconInfo,
    getMomentFromMs,
    getPastMonth,
    getTimelyData,
    isCurrentMonth,
    isSingleMonthSelected,
    isUpdatable,
    isViewable,
    isWholeYearSelected,
    missingCacvMode,
    missingCosts,
    missingRedistributionSettings,
    missingReiRedistribution,
    missingTipQuoteWithType,
    mkBillingPeriodParams,
    mkConsolidatedMonths,
    mkEnergyRatioChart,
    mkSharedChart,
    mkYearlyValue,
    sanityFromTimeActivationDate,
    sanityToTime,
    YearSummaryConsolidation
} from '../../../shared/ec-economics';
import {ComputationStatusService} from '../../../shared/compute-status.service';
import {ApexChart, ApexLegend, ApexXAxis} from 'ng-apexcharts';
import {ApexStroke} from 'ng-apexcharts/lib/model/apex-types';


export const CUSTOM_DATE_FORMATS = {
    parse: {
        dateInput: 'MM/YYYY',
    },
    display: {
        dateInput: 'MM/YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    }
};

export interface ChartQuantity {
    name: string;
    data: ChartLine[];
    uom: string;
}


const USE_FILTER = true;
const DIALOG_MAX_WIDTH = 1000;
const DIALOG_MIN_WIDTH = 700;
const NULL_DATA = '€ -';
const NULL_PERCENTAGE = '- %';

@Component({
    selector: 'app-ec-economics-aggregated',
    templateUrl: './ec-economics-aggregated.component.html',
    styleUrls: ['./ec-economics-aggregated.component.scss'],
    providers: [
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
        },
        {provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS}
    ]
})
export class ECEconomicsAggregatedComponent implements OnInit, OnDestroy {

    private destroyed = new Subject<void>();
    private entityId: string;
    private nvp: NiceValuePipe;
    private entityName: string;
    private templateEntity: HierarchyEntity = null;
    private templatePeriod: BillingPeriod = null;
    private gseDataSubj: BehaviorSubject<GseData> = new BehaviorSubject<GseData>(null);
    private costsSubj: BehaviorSubject<ECCosts> = new BehaviorSubject<ECCosts>(null);
    private membershipTrigger: BehaviorSubject<void> = new BehaviorSubject<void>(null);
    private isSingleMonth = false;
    private reloadEcoData = new Subject<void>();
    private reloadEcoGraphs: BehaviorSubject<boolean> = new BehaviorSubject(null);
    private activationDate: number = null;

    @ViewChild('enterGseData') private gseDataDialog: TemplateRef<any>;
    @ViewChild('dialogSelectMonth') private dialogSelectMonth: TemplateRef<any>;
    @ViewChild('costsSetup') private costsDialog: TemplateRef<any>;
    @ViewChild('ecWaitingForApproval') private ecWaitingForApprovalDialog: TemplateRef<any>;
    @ViewChild('membershipDialog') private membershipDialog: TemplateRef<any>;

    hierarchyEntity: Observable<HierarchyEntity> = null;
    billingPeriodCfg: BillingPeriodConfig = null;
    billingPeriod: BillingPeriod = null;
    billingPeriodSubj: ReplaySubject<BillingPeriod> = new ReplaySubject<BillingPeriod>(1);
    singleDate: Moment = null;
    timeZone: string = null;
    response: ECEconomicsAggregatedResponse = null;
    currentMonth = false;
    gasTitle: string;
    co2Title: string;
    futureEc = false;
    inactiveEc = false;
    isWholeYear = false;
    totalPods = 0;
    membershipFeeEnable = false;
    isRepresentative = false;
    dataSourceInfo: DataSourceInfo;
    showComputationWarning = false;
    summaryResponse: YearSummaryConsolidation[] = [];
    summaryResponseSubject: BehaviorSubject<YearSummaryConsolidation[]> = new BehaviorSubject<YearSummaryConsolidation[]>([]);
    energiesChartOptions: AxisChartOptions = {} as AxisChartOptions;
    incentivesChartOptions: AxisChartOptions = {} as AxisChartOptions;
    energyRatioChartOptions: AxisChartOptions = {} as AxisChartOptions;
    sharedEnergyEstimatedData = true;
    sharedEnergyConsolidatedData = true;
    incentiveEstimatedData = true;
    incentiveConsolidatedData = true;
    energyRatioEstimatedData = true;
    energyRatioConsolidatedData = true;

    // exported in HTML
    readonly ECSurplusPremiumRateScope = ECSurplusPremiumRateScope;
    readonly EconomicsErrorType = EconomicsErrorType;
    readonly USE_FILTER = USE_FILTER;
    readonly ECMode = ECMode;
    readonly hasError = hasError;
    readonly showError = showError;
    readonly NULL_DATA = NULL_DATA;
    readonly NULL_PERCENTAGE = NULL_PERCENTAGE;
    readonly ECCostType = ECCostType;
    readonly ECMemberParticipationType = ECMemberParticipationType;
    readonly CONTENT_DIALOG = CONTENT_DIALOG;
    readonly getMomentFromMs = getMomentFromMs;
    readonly getPastMonth = getPastMonth;
    readonly getSum = getSum;
    readonly CACV_STRING = CACV_STRING;
    readonly CPR_BT_STRING = CPR_BT_STRING;
    readonly CPR_MT_STRING = CPR_MT_STRING;
    readonly EntitySubType = EntitySubType;
    readonly TimeFilterType = TimeFilterType;
    readonly isDefined = isDefined;
    readonly isUpdatable = isUpdatable;
    readonly isViewable = isViewable;
    readonly missingCosts = missingCosts;
    readonly missingReiRedistribution = missingReiRedistribution;
    readonly missingTipQuoteWithType = missingTipQuoteWithType;
    readonly missingRedistributionSettings = missingRedistributionSettings;
    readonly missingCacvMode = missingCacvMode;
    readonly consolidationsAvailable = consolidationsAvailable;


    constructor(@Inject(APP_CONFIG_TOKEN) private readonly cfg: AppConfig,
                private readonly hierarchyService: HierarchyService,
                private readonly apiService: SmagApiService,
                private readonly msgBoxService: MsgBoxService,
                private readonly configurationService: ConfigurationService,
                private readonly i18n: I18NService,
                private readonly auth: AuthService,
                private readonly dialogService: DialogService,
                private readonly dialog: MatDialog,
                private readonly router: Router,
                private readonly changeDetectorRef: ChangeDetectorRef,
                private readonly computationService: ComputationStatusService) {
        this.isRepresentative = this.auth.profile.scopes?.includes(Scope.REPRESENTATIVE);
        this.nvp = new NiceValuePipe(i18n);
    }

    ngOnInit() {
        this.configurationService.timeConfig.pipe(first())
            .subscribe(timeCfg => this.timeZone = timeCfg.time_zone);
        this.configurationService.economicsConfig.pipe(first()).subscribe(ecoCfg => {
            this.co2Title = this.i18n.translate('CO2 emission avoided by producing electricity from renewable sources.') + '\n' +
                this.i18n.translate('It can be estimated by multiplying the energy produced by the value of CO2 emitted to produce a fossil kWh of {0}.', this.nvp.transform(ecoCfg.co2_E_ef, 'gCO2/kWh')) + '\n' +
                this.i18n.translate('This coefficient also contemplates the amount of CO2 emitted during the life cycle of the PV panel.');
            this.gasTitle = this.i18n.translate('Gas not used for electricity production as it is replaced by renewable source.') + '\n' +
                this.i18n.translate('It is estimated by dividing the electricity produced by the energy contained in natural gas') +
                this.i18n.translate(' which taking into account the average efficiency of thermal power plants is {0}.', this.nvp.transform(1 / ecoCfg.gas_E_ef, 'kWh/Smc'));
        });

        this.computationService.showComputingWarning
            .pipe(takeUntil(this.destroyed))
            .subscribe(bool => this.showComputationWarning = bool);

        // NOTE: as we store time range in URL, we need to avoid "navigation" to same entity when
        //       setting URL parameters for time-range
        this.hierarchyEntity = this.hierarchyService.selection.pipe(
            distinctUntilChanged((h1, h2) => h1?.id === h2?.id),
            shareReplay(1),
            takeUntil(this.destroyed)
        );
        this.hierarchyEntity.pipe(
            filter(he => !!he),
            takeUntil(this.destroyed)
        ).subscribe(he => {
            this.totalPods = he.children.filter((h => h.type === EntityType.POD)).length;
            this.entityId = he.id;
            this.entityName = he.name;
            if (can(he.permissions, Permission.READ)) {
                this.loadDataSourceInfo(he.id);
            }
        });

        combineLatest([
            this.hierarchyEntity,
            this.billingPeriodSubj,
            this.configurationService.timeConfig,
            this.gseDataSubj,
            this.costsSubj,
            this.configurationService.economicsConfig,
            this.membershipTrigger
        ]).pipe(takeUntil(this.destroyed))
            .subscribe(([hierarchyEntity,
                            billingPeriod,
                            timeCfg,
                            data,
                            costs,
                            ecoCfg
                        ]) => {
                this.billingPeriod = (billingPeriod as BillingPeriod);
                const timeZone = (timeCfg as TimeConfig).time_zone;
                this.isSingleMonth = isSingleMonthSelected(this.billingPeriod, timeZone, this.showNullData);
                this.currentMonth = isCurrentMonth(moment.tz((this.billingPeriod).fromTime, timeZone), timeZone);
                this.membershipFeeEnable = isDefined((ecoCfg as EconomicsConfig).membership_fee) && (ecoCfg as EconomicsConfig).membership_fee;
                this.mkData((hierarchyEntity as HierarchyEntity), this.billingPeriod);
                this.templateEntity = hierarchyEntity as HierarchyEntity;
                this.templatePeriod = this.billingPeriod;
                this.singleDate = moment.tz(this.billingPeriod.fromTime, timeZone);
            });

        this.hierarchyEntity.pipe(
            filter(hierarchyEntity => !!hierarchyEntity),
            switchMap(hierarchyEntity => can(hierarchyEntity.permissions, Permission.READ) ?
                this.apiService.getEntity(hierarchyEntity.id) : of(null)),
            takeUntil(this.destroyed)
        ).subscribe(entity => {
            this.activationDate = (entity as EnergyCommunity)?.activation_date || null;
            const now = moment.tz(this.timeZone).valueOf();
            this.billingPeriodCfg = {
                pastDate: this.activationDate || null,
                futureDate: null
            };
            if (!isDefined(this.billingPeriodCfg.pastDate)) {
                this.inactiveEc = true;
                this.showWaitingForApprovalModal();
            } else {
                this.inactiveEc = false;
                if (this.billingPeriodCfg.pastDate > now) {
                    this.futureEc = true;
                    this.showWaitingForApprovalModal();
                } else {
                    this.futureEc = false;
                }
            }
        });
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    onChangeBillingPeriod(period: BillingPeriod) {
        this.billingPeriodSubj.next(period);
        this.billingPeriod = period;
        this.isWholeYear = isWholeYearSelected(period.fromTime, period.toTime, this.timeZone, period.type);
        this.router.navigate([],
            {
                queryParams: mkBillingPeriodParams(period),
                queryParamsHandling: 'merge'
            }).then();
    }

    openGSEDialog() {
        this.dialogService.open<any, GseData>({
            title: this.i18n.translate('Upload Excel file of GSE with economic and energy data'),
            template: this.gseDataDialog,
            context: {}
        }).closed.then(data => {
            if (data) {
                // TODO add api request to enter gse data when BE is ready
                Promise.all([]
                ).then(() => {
                        this.msgBoxService.info(this.i18n.translate('Economic data entered correctly'));
                        this.gseDataSubj.next(data);
                    }
                ).catch(err => this.msgBoxService.error(this.i18n.translate('An error occurred while setting economic data'), err));
            }
        });
    }

    onClickEnterGseData() {
        if (this.isSingleMonth) {
            this.openGSEDialog();
        } else {
            this.dialogService.open<any, Moment>({
                title: this.i18n.translate('Select a month to insert GSE data'),
                template: this.dialogSelectMonth,
                context: {}
            }).closed.then(data => {
                if (data) {
                    this.singleDate = data;
                    this.openGSEDialog();
                }
            });
        }
    }

    onClickPlantsDetails() {
        this.router.navigate(['/c', 'ec', 'economics', 'plants'],
            {queryParamsHandling: 'merge'}).then();
    }

    onClickMarketIndicators() {
        this.router.navigate(['/c', 'ec', 'economics', 'market'], {queryParamsHandling: 'merge'}).then();
    }

    onClickCostsSetup() {
        this.dialogService.open<any, ECCosts>({
            title: this.i18n.translate('Costs setup'),
            template: this.costsDialog,
            context: {}
        }).closed.then(costs => {
            if (costs) {
                const value: YearlyValue = {
                    name: COSTS,
                    object_id: this.entityId,
                    object_type: ValueObjectType.RESOURCE,
                    value: costs,
                    year: moment.tz(this.billingPeriod.fromTime, this.timeZone).year()
                };
                this.apiService.setYearlyValue(value).then(() => {
                        this.msgBoxService.info(this.i18n.translate('Costs set up correctly'));
                        this.costsSubj.next(costs);
                    }
                ).catch(err => this.msgBoxService.error(this.i18n.translate('An error occurred while setting costs'), err));
            }
        });
    }

    showWaitingForApprovalModal() {
        this.dialog.open(this.ecWaitingForApprovalDialog, {
            maxWidth: `${DIALOG_MAX_WIDTH}px`,
            minWidth: `${DIALOG_MIN_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe();
    }

    exportEconomicsToExcel() {
        // TODO if logged user is representative, the exported excel will not contains rei of member or third plants
        this.apiService.exportEconomicsToExcel(ECEconomicsTemplateType.RECAP,
            this.templateEntity.id,
            this.templatePeriod.fromTime,
            this.templatePeriod.toTime,
            this.entityName)
            .subscribe(([data, filename]) => {
                downloadBlob(data, filename);
                this.msgBoxService.info(this.i18n.translate('File downloaded!'));
            }, error => {
                this.msgBoxService.error(this.i18n.translate('An error occurred while fetching the template'));
            });
    }

    onClickMembershipSetup() {
        this.dialogService.open<any, number>({
            title: this.i18n.translate('Configure the membership fee'),
            template: this.membershipDialog,
            context: {}
        }, {
            width: DIALOG_MAX_WIDTH
        }).closed.then(membershipFee => {
            if (membershipFee !== null && isNumber(membershipFee)) {
                this.apiService
                    .setYearlyValue(mkYearlyValue(MEMBERSHIP_FEE, membershipFee, this.entityId, this.singleDate.year()))
                    .then(() => {
                            this.msgBoxService.info(this.i18n.translate('Membership fee entered correctly'));
                            this.membershipTrigger.next();
                        }
                    ).catch(err => this.msgBoxService.error(this.i18n.translate('An error occurred while setting membership fee'), err));
            } else if (membershipFee === null) {
                this.apiService
                    .setYearlyValue(mkYearlyValue(MEMBERSHIP_FEE, null, this.entityId, this.singleDate.year()))
                    .then(() => {
                            this.msgBoxService.info(this.i18n.translate('Membership fee reset correctly'));
                            this.membershipTrigger.next();
                        }
                    ).catch(err => this.msgBoxService.error(this.i18n.translate('An error occurred while setting membership fee'), err));
            }
        });
    }

    disableCostsConfiguration(entity: HierarchyEntity) {
        return !this.isWholeYear || !isUpdatable(entity) || this.showNullData;
    }

    computeDifference(firstValue: number, secondValue: number) {
        const difference = secondValue - firstValue;
        const average = (firstValue + secondValue) / 2;
        return (difference / average) * 100;
    }

    updateEnergyConsolidated(checked: boolean) {
        this.sharedEnergyConsolidatedData = checked;
        this.reloadEcoGraphs.next(checked);
    }

    updateEnergyEstimated(checked: boolean) {
        this.sharedEnergyEstimatedData = checked;
        this.reloadEcoGraphs.next(checked);
    }

    updateIncentiveConsolidated(checked: boolean) {
        this.incentiveConsolidatedData = checked;
        this.reloadEcoGraphs.next(checked);
    }

    updateIncentiveEstimated(checked: boolean) {
        this.incentiveEstimatedData = checked;
        this.reloadEcoGraphs.next(checked);
    }

    updateEnergyRatioConsolidated(checked: boolean) {
        this.energyRatioConsolidatedData = checked;
        this.reloadEcoGraphs.next(checked);
    }

    updateEnergyRatioEstimated(checked: boolean) {
        this.energyRatioEstimatedData = checked;
        this.reloadEcoGraphs.next(checked);
    }

    get showNullData() {
        return this.inactiveEc || this.futureEc;
    }

    get hasNoEcPlants() {
        return !isDefined(this.response) || this.response.plants.ec_plants_count === 0;
    }

    get hasNoMemberPlants() {
        return !isDefined(this.response) || this.response.plants.member_plants_count === 0;
    }

    get hasNoThirdPlants() {
        return !isDefined(this.response) || this.response.plants.third_party_plants_count === 0;
    }

    get computePayingMembers() {
        return this.response.total_membership_fee / this.response.membership_fee;
    }

    get redistributedWithdrawals() {
        const rei = this.response.redistribution.redistributable_ec_plants_rei || 0;
        const tip = this.response.redistribution.redistributable_tip || 0;
        const cacv = this.response.redistribution.redistributable_c_acv || 0;
        return rei + tip + cacv;
    }

    private loadDataSourceInfo(networkId: string) {
        this.apiService.communityPodsStatistics(networkId).pipe(
            map(stats => mkMode(stats))
        ).subscribe(ecMode => this.dataSourceInfo = getDataSourceIconInfo(ecMode));
    }

    private mkData(entity: HierarchyEntity, billingPeriod: BillingPeriod) {
        this.response = null;
        if (entity?.type === EntityType.ENERGY_COMMUNITY && isViewable(entity)) {
            const fromTime = sanityFromTimeActivationDate(billingPeriod.fromTime, this.activationDate, this.timeZone);
            const toTime = sanityToTime(this.timeZone, billingPeriod.toTime);
            if (!this.inactiveEc && !this.futureEc) {
                // Ask API for computation readiness
                const currentYear = moment.tz(billingPeriod.fromTime, this.timeZone).startOf('month').year();
                this.reloadEcoData.next();
                this.computationService.checkComputationStatus(entity.id, currentYear).pipe(
                    takeUntil(merge(this.destroyed, this.reloadEcoData)),
                    switchMap(() => combineLatest([
                        this.apiService.ecEconomicsAggregated(entity.id, fromTime, toTime),
                        this.apiService.getYearlyValue(mkYearlyValue(CACV_MODE, null, entity.id, moment.tz(fromTime, this.timeZone).year())).pipe(
                            map(yearlyValue => (yearlyValue.value as ECCacvRedistribution).mode),
                            catchError(error => {
                                if (error.status === 404) {
                                    return of(ECCacvRedistributionMode.UNDEFINED);
                                }
                                throw error;
                            })),
                        this.reloadEcoGraphs.asObservable()
                    ])),
                    takeUntil(merge(this.destroyed, this.reloadEcoData)),
                    switchMap(([aggregatedResponse, cacvMode, reloadGraphs]) => {
                        this.response = aggregatedResponse;

                        if (!consolidationsAvailable(this.response)) {
                            this.sharedEnergyConsolidatedData = false;
                            this.incentiveConsolidatedData = false;
                            this.energyRatioConsolidatedData = false;
                        }
                        this.energiesChartOptions = mkSharedChart(this.cfg, this.i18n, this.response,
                            this.response.plants.non_incentivized_plants_count > 0,
                            this.sharedEnergyEstimatedData, this.sharedEnergyConsolidatedData, false, true);
                        this.energyRatioChartOptions = mkEnergyRatioChart(this.cfg, this.i18n, this.response,
                            this.response.plants.non_incentivized_plants_count > 0,
                            this.energyRatioEstimatedData, this.energyRatioConsolidatedData);
                        this.incentivesChartOptions = this.mkIncentiveChart(getTimelyData(this.response));
                        return mkConsolidatedMonths(entity.id, this.billingPeriodCfg.pastDate, this.timeZone, this.apiService);
                    }),
                    map(summaryResponse => {
                        this.summaryResponseSubject.next(summaryResponse);
                        this.summaryResponse = summaryResponse;
                    })
                ).subscribe();
            } else {  // community isn't active, fabricate the results
                this.generateInactiveEcoAggregatedData();
            }
        }
        this.changeDetectorRef.detectChanges();
    }

    private generateInactiveEcoAggregatedData() {
        // this generates data so that the page still renders and no errors are raised
        this.response = {
            costs: {} as ECEconomicsAggregatedCosts,
            energy: {} as ECEconomicsAggregatedEnergy,
            hourly_data: {} as ECEconomicsAggregatedTimelyData,
            daily_data: {} as ECEconomicsAggregatedTimelyData,
            gse: null,
            errors: [],
            incentivation: {} as ECEconomicsAggregatedIncentivation,
            params: {} as ECEconomicsParameters,
            redistribution: {} as ECEconomicsAggregatedRedistribution,
            total_membership_fee: null,
            plants: null,
            monthly_data: {} as ECEconomicsAggregatedTimelyData,
            weekly_data: {} as ECEconomicsAggregatedTimelyData,
            surplus_premium_rate: {} as ECEconomicsSurplusPremiumRate
        };
    }

    private mkIncentiveChart(timelyData: ECEconomicsAggregatedTimelyData) {


        const {series, colors} = this.extractIncentiveSeriesAndColors(timelyData);
        const it = require('apexcharts/dist/locales/it.json');
        const en = require('apexcharts/dist/locales/en.json');

        return {
            series,
            chart: {
                type: 'line',
                height: 350,
                stacked: false,
                locales: [it, en],
                defaultLocale: 'it',
                toolbar: {
                    tools: {
                        download: false,
                        selection: true,
                        zoom: true,
                        zoomin: true,
                        zoomout: true,
                        pan: true,
                        reset: true,
                    }
                }
            } as ApexChart,
            stroke: {
                show: true,
                curve: 'smooth',
                width: 2,
            } as ApexStroke,
            markers: {
                size: 0
            },
            dataLabels: {
                enabled: false
            },
            tooltip: {
                enabled: true,
                shared: true,
                followCursor: false,
                hideEmptySeries: true,
            },
            plotOptions: {},
            xaxis: {
                type: 'datetime',
                labels: {
                    datetimeUTC: false,
                }
            } as ApexXAxis,
            fill: {},
            colors,
            yaxis: {
                labels: {
                    formatter(y) {
                        if (isDefined(y)) {
                            return (y).toFixed(2) + ' €';
                        }
                        return y;
                    }
                }
            },
            legend: {
                position: 'bottom',
                horizontalAlign: 'center'
            } as ApexLegend
        };
    }

    private extractIncentiveSeriesAndColors(timelyData: ECEconomicsAggregatedTimelyData) {
        const {
            tip,
            cacv,
            totalIncentive,
            consolidatedTip,
            consolidatedCacv,
            consolidatedTotalIncentive,
        } = this.extractIncentives(timelyData);

        let series = [
            this.incentiveEstimatedData ? {
                name: this.i18n.translate('Total (estimated)'),
                data: totalIncentive,
                type: 'line'
            } : null,
            this.incentiveEstimatedData ? {
                name: this.i18n.translate('TIP (estimated)'),
                data: tip,
                type: 'line'
            } : null,
            this.incentiveEstimatedData ? {
                name: this.i18n.translate('Cacv (estimated)'),
                data: cacv,
                type: 'line'
            } : null,
            this.incentiveConsolidatedData ? {
                name: this.i18n.translate('Total (billed)'),
                data: consolidatedTotalIncentive,
                type: 'line'
            } : null,
            this.incentiveConsolidatedData ? {
                name: this.i18n.translate('TIP (billed)'),
                data: consolidatedTip,
                type: 'line'
            } : null,
            this.incentiveConsolidatedData ? {
                name: this.i18n.translate('Cacv (billed)'),
                data: consolidatedCacv,
                type: 'line'
            } : null,
        ];
        let colors = [
            this.incentiveEstimatedData ? this.cfg.shared.colors.totalIncentive : null,
            this.incentiveEstimatedData ? this.cfg.shared.colors.tip : null,
            this.incentiveEstimatedData ? this.cfg.shared.colors.cacv : null,
            this.incentiveConsolidatedData ? this.cfg.shared.colors.consolidatedIncentive : null,
            this.incentiveConsolidatedData ? this.cfg.shared.colors.consolidatedTIP : null,
            this.incentiveConsolidatedData ? this.cfg.shared.colors.consolidatedCacv : null,
        ];

        series = series.filter(s => isDefined(s));
        colors = colors.filter(c => isDefined(c));

        return {series, colors};
    }

    private zerosDecimals(num: number) {
        return num < 0.01 ? 0.00 : num;
    }

    private extractIncentives(timelyData: ECEconomicsAggregatedTimelyData) {
        const tip = [];
        const cacv = [];
        const totalIncentive = [];
        const consolidatedTip = [];
        const consolidatedCacv = [];
        const consolidatedTotalIncentive = [];

        for (let i = 0; i < timelyData.times.length; i++) {
            tip.push({x: timelyData.times[i], y: this.zerosDecimals(timelyData.estimated_tip[i])});
            cacv.push({x: timelyData.times[i], y: this.zerosDecimals(timelyData.estimated_cacv[i])});
            totalIncentive.push({
                x: timelyData.times[i],
                y: this.zerosDecimals(timelyData.estimated_total_incentive[i])
            });
            consolidatedTip.push({
                x: timelyData.times[i],
                y: allGSE(timelyData, i) ? this.zerosDecimals(timelyData.tip[i]) : null
            });
            consolidatedCacv.push({
                x: timelyData.times[i],
                y: allGSE(timelyData, i) ? this.zerosDecimals(timelyData.cacv[i]) : null
            });
            consolidatedTotalIncentive.push({
                x: timelyData.times[i],
                y: allGSE(timelyData, i) ? this.zerosDecimals(timelyData.total_incentive[i]) : null
            });
        }
        return {
            tip,
            cacv,
            totalIncentive,
            consolidatedTip,
            consolidatedCacv,
            consolidatedTotalIncentive
        };
    }

}
